/* eslint-disable no-restricted-imports */
import React, {useRef, useState} from 'react'
import {Button} from '../../../../common/Button/Button'
import styles from './ActionButtons.module.scss'
import {useUIGameEndContext} from '../../../../contexts/UIGameEndContext'
import {T} from '../../../../common/T'
import clsx from 'clsx'
import {CA} from '../../../../../../../code/client/shared/scripts/analytics/Analytics'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../../../code/shared/entity_datas/EntityDatas'
import {ReplayInitiationData} from '@colonist/game-base-types'
import {UIAPIEndpoints} from '../../../../../../../code/client/shared/scripts/network/UIAPIEndpoints'
import {UIAjaxWrapper} from '../../../../../../../code/client/shared/scripts/network/UIAjaxWrapper'
import {socket} from '../../../../../../../code/client/shared/scripts/EntryShared'
import {UIReplayController} from '../../../../../../../code/client/shared/scripts/ui_replay/UIReplayController'
import {useUIGameManagerContext} from '../../../../contexts/UIGameManagerContext'

export function MapReplayButton({setShowOpenStatsButton}: {setShowOpenStatsButton: (open: boolean) => void}) {
    const gameManager = useUIGameManagerContext()
    const {gameEndData, toggleEndGameScreen} = useUIGameEndContext()
    const [isReplayReady, setIsReplayReady] = useState(false)
    const [isReplayDataUnavailable, setIsReplayDataUnavailable] = useState(false)
    const replayControllerRef = useRef<UIReplayController | null>(null)

    const getReplayData = async(): Promise<ReplayInitiationData | undefined> => {
        const replayDataUrl = UIAPIEndpoints.apiReplayDataFromGameId(socket.gameListener.databaseGameId, gameManager.gameController.myColor)
        const displayError = false
        const replayDataResponse = await UIAjaxWrapper.getRequestHandler(replayDataUrl, undefined, undefined, displayError)

        if(replayDataResponse == undefined || !replayDataResponse.isSuccess) return undefined
        const replayData = replayDataResponse.json.data as ReplayInitiationData
        replayData.rankedUserStates = gameEndData.rankedUserStates

        return replayDataResponse.json.data
    }

    const startReplay = (replayData: ReplayInitiationData) => {
        gameManager.resetGameUIToRebuildOnReconnect()

        // Original issue: https://github.com/colonistio/katan/issues/17913
        // Temporary fix for Discord, overwrite PlayerStates with the ones used in the game since these have the correct discord usernames
        const recentPlayerStates = gameManager.gameController.getPlayerValidators().map((playerValidator) => playerValidator.userState)
        replayData.playerUserStates = recentPlayerStates

        const isLiveGame = true
        const replayController = new UIReplayController(replayData, socket.actionController, isLiveGame)
        const actionIndexBeforeEndgame = replayController.actionIndexForEndgame - 1
        replayController.setAction(actionIndexBeforeEndgame, 'instant', false)
        replayControllerRef.current = replayController
    }

    const setLastActionAndHideEndgameScreen = () => {
        if(replayControllerRef.current) {
            const actionIndexBeforeEndgame = replayControllerRef.current.
                actionIndexForEndgame - 1
            replayControllerRef.current.setAction(actionIndexBeforeEndgame, 'instant', false)
            replayControllerRef.current?.hideEndGameScreen()
        }
    }

    const onMapReplayClick = async() => {
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameReturnToMap)

        void CA.sendAnalyticEventOnce({
            type: AnalyticEventType.EndGameReturnToMapButton,
            category: AnalyticEventCategory.Game,
        })

        if(gameEndData.isReplayAvailable && !isReplayDataUnavailable) {
            if(!replayControllerRef.current) {
                const existedReplayController = gameManager.replayView?.replayController
                if(existedReplayController) {
                    setIsReplayReady(true)
                    replayControllerRef.current = existedReplayController
                    setLastActionAndHideEndgameScreen()
                    return
                }
            }

            if(isReplayReady && replayControllerRef.current) {
                setLastActionAndHideEndgameScreen()
            } else {
                toggleEndGameScreen(false)
                const replayData = await getReplayData()
                if(replayData) {
                    startReplay(replayData)
                    setIsReplayReady(true)
                } else {
                    setIsReplayDataUnavailable(true)
                    setShowOpenStatsButton(true)
                }
            }
        } else {
            setShowOpenStatsButton(true)
            toggleEndGameScreen(false)
        }
    }

    return (
        <Button
            className={clsx(styles.button)}
            onClick={onMapReplayClick}
            variant='dark'>
            {gameEndData.isReplayAvailable ?
                <T id={{key: 'strings:game.endGame.cornerButton.mapWithReplay'}}/>
                : <T id={{key: 'strings:game.endGame.cornerButton.map'}}/>}
        </Button>
    )
}
